import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import api from "@/axiosSetup";
import { IProject } from "../../../types/IProject";

// type ProjectSearchParams = {
//   search?: string;
//   metadata?: string[];
//   archivedMode?: "active" | "archived" | "include";
// };
type FetchProjectsArgs = {
  search?: string;
  searchTerms?: string[];
  metadata?: string[];
  archivedMode?: "active" | "archived" | "include";
};

type CreateProjectPayload = {
  title: string;
  description?: string | null;
  metadata?: string[];
  bcrs?: string[];
  meetingMinutes?: string[];
};

type UpdateProjectPayload = {
  projectId: string;
  updates: Partial<{
    title: string;
    description: string | null;
    metadata: string[];
    bcrs: string[];
    meetingMinutes: string[];
    archived: boolean;
  }>;
};

interface ProjectsState {
  projects: IProject[];
  currentProject: IProject | null;
  loading: boolean;
  initLoading: boolean;
  saving: boolean;
  error: string | null;
  lastSearch: FetchProjectsArgs | null;
}

const initialState: ProjectsState = {
  projects: [],
  currentProject: null,
  loading: false,
  initLoading: false,
  saving: false,
  error: null,
  lastSearch: null,
};

const getErrorMessage = (err: unknown, fallback: string) => {
  const error = err as AxiosError<{ message: string }>;
  return error.response?.data?.message || fallback;
};

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    setCurrentProject: (state, action: PayloadAction<IProject | null>) => {
      state.currentProject = action.payload;
    },

    clearCurrentProject: (state) => {
      state.currentProject = null;
    },

    clearProjects: (state) => {
      state.projects = [];
      state.currentProject = null;
      state.loading = false;
      state.initLoading = false;
      state.saving = false;
      state.error = null;
      state.lastSearch = null;
    },

    setProjectsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    removeBcrFromCurrentProject: (
      state,
      action: PayloadAction<{ bcrId: string }>
    ) => {
      if (!state.currentProject) return;

      state.currentProject.bcrs = state.currentProject.bcrs.filter(
        (bcr: any) => String(bcr._id) !== String(action.payload.bcrId)
      );

      state.currentProject.bcrFiles = state.currentProject.bcrFiles?.filter(
        (file: any) => String(file.bcrContent?._id) !== String(action.payload.bcrId)
      );
    },

    removeMeetingMinuteFromCurrentProject: (
      state,
      action: PayloadAction<{ meetingMinuteId: string }>
    ) => {
      if (!state.currentProject) return;

      state.currentProject.meetingMinutes = state.currentProject.meetingMinutes.filter(
        (mm: any) => String(mm._id) !== String(action.payload.meetingMinuteId)
      );

      state.currentProject.meetingMinuteFiles =
        state.currentProject.meetingMinuteFiles?.filter(
          (file: any) =>
            String(file.meetingMinuteContent?._id) !==
            String(action.payload.meetingMinuteId)
        );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.lastSearch = action.meta.arg || null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch projects.";
      })

      .addCase(fetchProjectById.pending, (state) => {
        state.initLoading = true;
        state.error = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.initLoading = false;
        state.currentProject = action.payload;
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.initLoading = false;
        state.error = action.payload || "Failed to fetch project.";
      })

      .addCase(createProjectAction.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createProjectAction.fulfilled, (state, action) => {
        state.saving = false;
        state.projects.unshift(action.payload);
        state.currentProject = action.payload;
      })
      .addCase(createProjectAction.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to create project.";
      })

      .addCase(updateProjectAction.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateProjectAction.fulfilled, (state, action) => {
        state.saving = false;

        state.projects = state.projects.map((project) =>
          String(project._id) === String(action.payload._id)
            ? action.payload
            : project
        );

        if (
          state.currentProject &&
          String(state.currentProject._id) === String(action.payload._id)
        ) {
          state.currentProject = action.payload;
        }
      })
      .addCase(updateProjectAction.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to update project.";
      })

      .addCase(deleteProjectAction.fulfilled, (state, action) => {
        state.projects = state.projects.filter(
          (project) => String(project._id) !== String(action.payload)
        );

        if (
          state.currentProject &&
          String(state.currentProject._id) === String(action.payload)
        ) {
          state.currentProject = null;
        }
      })

      // Toggling archived stuff
      .addCase(toggleProjectArchiveAction.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(toggleProjectArchiveAction.fulfilled, (state, action) => {
        state.saving = false;

        state.projects = state.projects.map((project) =>
          String(project._id) === String(action.payload._id)
            ? action.payload
            : project
        );

        if (
          state.currentProject &&
          String(state.currentProject._id) === String(action.payload._id)
        ) {
          state.currentProject = action.payload;
        }
      })
      .addCase(toggleProjectArchiveAction.rejected, (state, action) => {
        state.saving = false;
        state.error =
          action.payload || "Failed to archive/unarchive project.";
      })
  },
});


export const fetchProjects = createAsyncThunk<
  IProject[],
  FetchProjectsArgs | undefined,
  { rejectValue: string }
>("projects/fetchProjects", async (params, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();

    if (params?.searchTerms?.length) {
      params.searchTerms.forEach((term) => {
        if (term.trim()) queryParams.append("search", term.trim());
      });
    } else if (params?.search?.trim()) {
      queryParams.append("search", params.search.trim());
    }

    if (params?.metadata?.length) {
      queryParams.append("metadata", params.metadata.join(","));
    }

    if (params?.archivedMode) {
      queryParams.append("archivedMode", params.archivedMode);
    }

    const query = queryParams.toString();
    const response = await api.get(`/api/projects/search${query ? `?${query}` : ""}`);

    return response.data.projects || [];
  } catch (err) {
    return rejectWithValue(getErrorMessage(err, "Failed to fetch projects."));
  }
});

export const fetchProjectById = createAsyncThunk<
  IProject,
  string | { projectId: string; summary: boolean },
  { rejectValue: string }
>("projects/fetchProjectById", async (request, { rejectWithValue }) => {
  try {
    const projectId = typeof request === 'string' ? request : request.projectId;
    const summary = typeof request !== 'string' && request.summary;
    const response = await api.get(`/api/projects/${projectId}${summary ? '?summary=true' : ''}`);
    return response.data.project;
  } catch (err) {
    return rejectWithValue(getErrorMessage(err, "Failed to fetch project."));
  }
});

export const createProjectAction = createAsyncThunk<
  IProject,
  CreateProjectPayload,
  { rejectValue: string }
>("projects/createProject", async (payload, { rejectWithValue }) => {
  try {
    const response = await api.post("/api/projects", payload);
    return response.data.project;
  } catch (err) {
    return rejectWithValue(getErrorMessage(err, "Failed to create project."));
  }
});

export const updateProjectAction = createAsyncThunk<
  IProject,
  UpdateProjectPayload,
  { rejectValue: string }
>("projects/updateProject", async ({ projectId, updates }, { rejectWithValue }) => {
  try {
    const response = await api.patch(`/api/projects/${projectId}`, updates);
    return response.data.project;
  } catch (err) {
    return rejectWithValue(getErrorMessage(err, "Failed to update project."));
  }
});

export const deleteProjectAction = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("projects/deleteProject", async (projectId, { rejectWithValue }) => {
  try {
    await api.delete(`/api/projects/${projectId}`);
    return projectId;
  } catch (err) {
    return rejectWithValue(getErrorMessage(err, "Failed to delete project."));
  }
});

export const toggleProjectArchiveAction = createAsyncThunk<
  IProject,
  string,
  { rejectValue: string }
>("projects/toggleArchive", async (projectId, { rejectWithValue }) => {
  try {
    const response = await api.patch(
      `/api/projects/${projectId}/toggle-archive`
    );

    return response.data.project;
  } catch (err) {
    return rejectWithValue(
      getErrorMessage(err, "Failed to archive/unarchive project.")
    );
  }
});

export const {
  setCurrentProject,
  clearCurrentProject,
  clearProjects,
  setProjectsError,
  removeBcrFromCurrentProject,
  removeMeetingMinuteFromCurrentProject,
} = projectsSlice.actions;

export default projectsSlice.reducer;
